import React, { useEffect } from 'react';
import { GridSubmitAction } from '@ctsu/ctsu-mui-grid';
import { MUISelect, MUITextBox } from 'components/FormHelpers/FormInputs';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { getEmptyValue, getUIDateFormat } from 'helpers';
import { withChangeHandlers } from 'helpers/Change';
import { MUIDatePicker } from 'components/FormHelpers/DatePicker';
import { compose } from 'recompose';
import {
  TableRow, TableCell,
  ListItem, ListItemText
} from '@material-ui/core';
import { useSelector } from 'react-redux';

const StepNumber = ({
  state, handleChange
}) => (
  <MUITextBox
    title="Step Number"
    value={state.step}
    onChange={handleChange}
    type="number"
    validators={['required', 'maxNumber:99']}
    errorMessages={['Required Field', 'Number must be up to 2 digits']}
    name="step"
    className="mui-input-sm"
  />
);

const Description = ({
  state, handleChange
}) => (
  <MUITextBox
    title="Step Description"
    multiline
    rowsMax="2"
    value={getEmptyValue(state.description)}
    onChange={handleChange}
    validators={['required', 'maxStringLength:250']}
    errorMessages={['Required Field', 'Entry must be up to 250 characters']}
    name="description"
    className="mui-input-md"
  />
);


const MinNonCreds = ({
  state, handleChange, name
}) => (
  <MUITextBox
    title="Minimum Non-required Credentials"
    type="number"
    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()} // dont allow these numerical characters in the number field
    defaultValue={state[name] || '0'}
    value={state[name]}
    onChange={handleChange}
    name={name}
    className="mui-input-md"
  />
);


const Required = ({ state: { isMandatory }, handleChange }) => (
  <MUISelect
    name="isMandatory"
    validators={['required']}
    errorMessages={['Required Field']}
    value={isMandatory === undefined ? null : isMandatory}
    onChange={handleChange}
    title="Mandatory"
  >
    <ListItem button value>
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false}>
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);

const CountTowards = ({ state: { isCountTowards }, handleChange }) => (
  <MUISelect
    name="isCountTowards"
    value={isCountTowards === undefined ? null : isCountTowards}
    onChange={handleChange}
    validators={['required']}
    errorMessages={['Required Field']}
    title="Count Towards"
  >
    <ListItem button value>
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false}>
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);

const StepType = ({ state, handleChange }) => (
  <MUISelect
    name="stepType"
    value={getEmptyValue(state && state.stepType)}
    onChange={handleChange}
    validators={['required']}
    errorMessages={['Required Field']}
    title="Step Type"
  >
    <ListItem button value="INTERVENTION">
      <ListItemText primary="Intervention" />
    </ListItem>
    <ListItem button value="SCREENING">
      <ListItemText primary="Screening" />
    </ListItem>
    <ListItem button value="OTHER">
      <ListItemText primary="Other" />
    </ListItem>
  </MUISelect>
);

const LoadTo = ({ state, handleChange }) => (
  <MUISelect
    name="loadTo"
    validators={['required']}
    errorMessages={['Required Field']}
    value={getEmptyValue(state && state.loadTo)}
    onChange={handleChange}
    title="Load To"
  >
    <ListItem button value="RAVE">
      <ListItemText primary="Rave" />
    </ListItem>
    <ListItem button value="OC">
      <ListItemText primary="OC" />
    </ListItem>
    <ListItem button value="NONE">
      <ListItemText primary="None" />
    </ListItem>
  </MUISelect>
);

const FM = ({ state: { isProcessedInFm }, handleChange }) => (
  <MUISelect
    name="isProcessedInFm"
    value={isProcessedInFm === undefined ? null : isProcessedInFm}
    validators={['required']}
    errorMessages={['Required Field']}
    onChange={handleChange}
    title="FM"
  >
    <ListItem button value>
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false}>
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);

const CaAers = ({ state: { isLoadToCaaers }, handleChange }) => {
  const openConfigs = useSelector(s => s.openData.openData.openConfig);
  const disableField = !(openConfigs && openConfigs.isOpenLdCaaers);

  return (
    <MUISelect
      name="isLoadToCaaers"
      validators={['required']}
      errorMessages={['Required Field']}
      value={isLoadToCaaers === undefined ? false : isLoadToCaaers}
      onChange={handleChange}
      title="CaAers"
      disabled={disableField}
    >
      <ListItem button value>
        <ListItemText primary="Yes" />
      </ListItem>
      <ListItem button value={false}>
        <ListItemText primary="No" />
      </ListItem>
    </MUISelect>
  );
}

const CM = ({ state, handleChange }) => (
  <MUISelect
    name="isCmStep"
    value={state.isCmStep === true}
    onChange={handleChange}
    title="Central Monitoring?"
  >
    <ListItem button value key="isCmStepYes">
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false} key="isCmStepNo">
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);

const Slot = ({ state, handleChange }) => (
  <MUISelect
    name="isSlot"
    value={state.isSlot === true}
    onChange={handleChange}
    title="Slot?"
  >
    <ListItem button value key="isSlotYes">
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false} key="isSlotNo">
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);
const ER = ({ state, handleChange }) => (
  <MUISelect
    name="isEligibilityReviewStep"
    value={state.isEligibilityReviewStep === true}
    onChange={handleChange}
    title="Eligibility Review?"
  >
    <ListItem button value key="isEligibilityReviewStepYes">
      <ListItemText primary="Yes" />
    </ListItem>
    <ListItem button value={false} key="isEligibilityReviewStepNo">
      <ListItemText primary="No" />
    </ListItem>
  </MUISelect>
);

const Start = ({
  state, handleDateChange
}) => (
  <MUIDatePicker
    title="Start Date"
    name="startDate"
    // validators={['required']}
    // errorMessages={['Required Field']}
    onChange={handleDateChange('startDate')}
    value={state.startDate ? getUIDateFormat(state.startDate) : ''}
  />
);

const End = ({
  state, handleDateChange
}) => {
  ValidatorForm.addValidationRule('minDate', value => !(value) || (value !== 'Invalid date' && value > getUIDateFormat(state.startDate)));
  return (
    <MUIDatePicker
      title="End Date"
      name="endDate"
      onChange={handleDateChange('endDate')}
      validators={(state && state.startDate && state.endDate) && ['minDate']}
      errorMessages={(state && state.startDate && state.endDate) && [
        'End date should be greater than start date'
      ]}
      value={state.endDate ? getUIDateFormat(state.endDate) : ''}
    />
  );
};

const EditREGSteps = (props) => {
  const {
    cancel,
    state,
    reset,
    handleChange,
    handleDateChange,
    setIsDisableEditButton
  } = props;
  
  useEffect(() => {
    setIsDisableEditButton(true);
    return () => {
      setIsDisableEditButton(false);
    }
  }, []);

  return (
    <TableRow>
      <TableCell>
        <StepNumber
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <Description
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <Required
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <CountTowards
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <StepType
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <LoadTo
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <FM
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <CaAers
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <Slot
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <CM
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <ER
          state={state}
          handleChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <Start
          state={state}
          handleDateChange={handleDateChange}
        />
      </TableCell>
      <TableCell>
        <End
          state={state}
          handleDateChange={handleDateChange}
        />
      </TableCell>
      <TableCell>
        <MinNonCreds
          name="minNonReqCred"
          state={state}
          handleChange={handleChange} />
      </TableCell>
      <TableCell>
        <MinNonCreds
          name="maxNonReqCred"
          state={state}
          handleChange={handleChange} />
      </TableCell>
      <TableCell align='center'>
        <GridSubmitAction cancel={cancel} reset={reset} />
      </TableCell>
    </TableRow>
  );
};


const EditRegSteps = compose(withChangeHandlers)(EditREGSteps);

export default EditRegSteps;
